/**
 * BackendService - Main service layer for book tracker
 * Coordinates between Google Books API and PocketBase database
 */

import { searchBooks, getBookDetails, getBookCover, getBookCoverOpenLibrary, getBookCoverByOLId, searchBookCoversOL } from './OpenLibraryClient.js';
import DatabaseRepository from './DatabaseRepository.js';
import { ProxyClient, fetchWithRetry, CatalogCache } from '@code-burrow/tracker-core';

export class BackendService {
    constructor(getToken) {
        const pocketbaseURL = import.meta.env.VITE_POCKETBASE_URL;
        if (!pocketbaseURL) throw new Error('VITE_POCKETBASE_URL is not set');

        this.proxy = new ProxyClient(getToken);
        this.db = new DatabaseRepository(pocketbaseURL, this.proxy);
        this._userIdCache = new Map();
    }

    get pocketbase() { return this.db.pb; }

    get _apiBase() {
        return import.meta.env.VITE_PROXY_URL
            ? `${import.meta.env.VITE_PROXY_URL.replace(/\/+$/, '')}/api`
            : '/api';
    }

    async _authHeaders(currentUserId) {
        const headers = {};
        if (currentUserId) headers['x-tracker-user-id'] = currentUserId;
        if (this.proxy._getToken) {
            try {
                const token = await this.proxy._getToken();
                if (token) headers['Authorization'] = `Bearer ${token}`;
            } catch (_) {}
        }
        return headers;
    }

    async getAggregatedCollection(currentUserId) {
        const headers = await this._authHeaders(currentUserId);
        return fetchWithRetry(`${this._apiBase}/collection/books`, { headers });
    }

    async clearCatalogCache() {
        try { await new CatalogCache('books').clear(); } catch {}
    }

    async getCatalog() {
        const headers = await this._authHeaders();
        return fetchWithRetry(`${this._apiBase}/collection/books/catalog`, { headers });
    }

    async getUserData(currentUserId) {
        const headers = await this._authHeaders(currentUserId);
        return fetchWithRetry(`${this._apiBase}/collection/books/userdata`, { headers });
    }

    /**
     * Re-download a book's cover at a specific Google Books zoom level.
     * @param {string} bookId - PocketBase book record ID
     * @param {number} zoom - 1 (small), 2 (medium), or 3 (large)
     */
    async changeBookCover(bookId, zoom) {
        try {
            const book = await this.db.getBook(bookId);
            if (!book?.googleBooksId) throw new Error('No Google Books ID');
            const { coverDataURL } = await getBookCover(book.googleBooksId, zoom);
            if (!coverDataURL) throw new Error('No cover returned');
            await this.db.updateBookWithImage(bookId, {}, coverDataURL);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    /**
     * Re-download a book's cover from Open Library by ISBN.
     * @param {string} bookId - PocketBase book record ID
     * @param {string} isbn - ISBN-10 or ISBN-13
     * @param {string} size - 'S', 'M', or 'L'
     */
    async changeBookCoverFromOL(bookId, isbn, size = 'L') {
        try {
            if (!isbn) throw new Error('No ISBN available for this book');
            // Strip hyphens/spaces — OL is more reliable with bare numeric ISBNs
            const cleanIsbn = isbn.replace(/[\s-]/g, '');
            const { coverDataURL } = await getBookCoverOpenLibrary(cleanIsbn, size);
            if (!coverDataURL) throw new Error('No cover returned');
            await this.db.updateBookWithImage(bookId, {}, coverDataURL);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async changeBookCoverByOLId(bookId, coverId, size = 'L') {
        try {
            if (!coverId) throw new Error('No cover ID provided');
            const { coverDataURL } = await getBookCoverByOLId(coverId, size);
            if (!coverDataURL) throw new Error('No cover returned');
            await this.db.updateBookWithImage(bookId, {}, coverDataURL);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async searchBookCoverOptions(title, author) {
        try {
            return await searchBookCoversOL(title, author);
        } catch {
            return { covers: [] };
        }
    }

    // -- Google Books Search --------------------------------------------------

    async searchBooks(query) {
        try {
            const items = await searchBooks(query);
            return { success: true, items };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getBookDetails(googleBooksId) {
        try {
            const details = await getBookDetails(googleBooksId);
            return { success: true, details };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // -- Collection CRUD -----------------------------------------------------

    async getAllBooks() {
        try {
            const books = await this.db.getAllBooks();
            return { success: true, books };
        } catch (error) {
            if (error.message?.includes('autocancelled')) return { success: true, books: [] };
            return { success: false, error: error.message };
        }
    }

    async getAllUserBooks() {
        try {
            const records = await this.db.pb.collection('user_books').getFullList({
                $autoCancel: false,
                batch: 500,
            });
            return { success: true, records };
        } catch {
            return { success: true, records: [] };
        }
    }

    async addBook(data) {
        try {
            // Check if already in collection
            const existing = await this.db.findByGoogleBooksId(data.googleBooksId);
            if (existing) return { success: true, book: existing, alreadyExists: true };

            const record = await this.db.createBook(data);
            return { success: true, book: record };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async updateBook(id, updates) {
        try {
            await this.db.updateBook(id, updates);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async deleteBook(id) {
        try {
            await this.db.deleteBook(id);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async refreshBook(id) {
        try {
            const book = await this.db.getBook(id);
            if (!book.googleBooksId) throw new Error('No Google Books ID — cannot refresh');
            const result = await this.getBookDetails(book.googleBooksId);
            if (!result.success) throw new Error(result.error);
            const details = result.details;
            await this.db.updateBookWithImage(id, {
                title: details.title,
                author: details.author,
                description: details.description,
                subjects: details.subjects,
                isbn: details.isbn,
                pageCount: details.pageCount,
                publishedDate: details.publishDate,
                coverURL: details.coverURL,
                goodreadsURL: details.goodreadsURL,
            }, details.coverDataURL);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // -- Join table CRUD (user_books) ----------------------------------------

    async resolveUserId(userName) {
        if (this._userIdCache.has(userName)) return this._userIdCache.get(userName);
        const record = await this.db.pb.collection('tracker_users')
            .getFirstListItem(`name="${userName}"`, { $autoCancel: false });
        this._userIdCache.set(userName, record.id);
        return record.id;
    }

    async upsertUserBook(userName, bookPbId, data) {
        const userId = await this.resolveUserId(userName);
        return this.proxy.collection('user_books').upsert({ book: bookPbId }, data, userId);
    }

    async deleteUserBook(userName, bookPbId) {
        const userId = await this.resolveUserId(userName);
        const record = await this.db.pb.collection('user_books')
            .getFirstListItem(`user="${userId}" && book="${bookPbId}"`, { $autoCancel: false });
        await this.proxy.collection('user_books').delete(record.id);
        return true;
    }

    // -- Lists CRUD ----------------------------------------------------------

    async getAllLists(userName) {
        try {
            const userId = await this.resolveUserId(userName);
            const records = await this.db.pb.collection('lists').getFullList({
                filter: `app="books" && user="${userId}"`,
                $autoCancel: false,
            });
            return { success: true, lists: records };
        } catch {
            return { success: true, lists: [] };
        }
    }

    async createList(userName, { name, emoji }) {
        const userId = await this.resolveUserId(userName);
        return this.proxy.collection('lists').create({
            name, emoji: emoji || '', app: 'books', user: userId,
        });
    }

    async updateList(listId, data) {
        return this.proxy.collection('lists').update(listId, data);
    }

    async deleteList(listId) {
        // Delete all items in the list first
        const items = await this.db.pb.collection('list_items').getFullList({
            filter: `list="${listId}"`, $autoCancel: false,
        });
        for (const item of items) {
            await this.proxy.collection('list_items').delete(item.id);
        }
        await this.proxy.collection('lists').delete(listId);
    }

    // -- List Items CRUD -----------------------------------------------------

    async getListItems(listId) {
        try {
            const records = await this.db.pb.collection('list_items').getFullList({
                filter: `list="${listId}"`,
                sort: 'position',
                $autoCancel: false,
            });
            return { success: true, items: records };
        } catch {
            return { success: true, items: [] };
        }
    }

    async addListItem(listId, { itemId, title, imageURL, notes }) {
        // Get next position
        const existing = await this.db.pb.collection('list_items').getFullList({
            filter: `list="${listId}"`, sort: '-position', $autoCancel: false,
        });
        const nextPos = existing.length > 0 ? (existing[0].position || 0) + 1 : 0;

        return this.proxy.collection('list_items').create({
            list: listId,
            itemId: itemId || '',
            title,
            imageURL: imageURL || '',
            notes: notes || '',
            position: nextPos,
        });
    }

    async updateListItem(id, data) {
        return this.proxy.collection('list_items').update(id, data);
    }

    async deleteListItem(id) {
        await this.proxy.collection('list_items').delete(id);
    }

    /**
     * Promote an unlinked list item to the collection.
     * Adds the book to the books collection and links the list item.
     */
    async promoteListItem(listItemId, bookData) {
        const result = await this.addBook(bookData);
        if (!result.success) throw new Error(result.error);
        await this.proxy.collection('list_items').update(listItemId, {
            itemId: result.book.id,
        });
        return result.book;
    }
}

export default BackendService;
